// historyView — build the History & Inspection surface's typed views from
// the server's `/history` wire payloads.
//
// Same posture as `workspaceOverview.ts`: the server hands the shell PLAIN
// JSON (it can't import @bilda/client's row types), and this pure module walks
// that untrusted shape with `wireShape` readers and lands it in the typed
// `HistoryView` / `FileHistoryView` the surface renders — parse, don't
// validate, at the boundary. No cast: each `kind` is dispatched into its
// concrete `TimelineRow` variant by hand.
//
// Clock-free: `when` / section labels are formatted from each moment's OWN
// ISO timestamp (deterministic parse), never from `Date.now()` — so this
// module stays pure and the impurity (the wall clock) never leaks in.
//
// MVP scope (documented shortcut): the real HTTP path populates the TIMELINE
// (and per-file version list) from folded events. The inspection lenses
// ("Knows you", Knowledge bank, Grounding) and per-version diffs are served
// EMPTY here — they are separate projections not yet exposed over HTTP; the
// mock keeps serving their design fixtures. The surface is fully reachable,
// honestly empty where a real projection isn't wired.

export { buildFileHistoryView, buildHistoryView };

import type { VersionEntry } from "../components/HistoryInspection/FileHistory/types.js";
import type {
    TimelineActor,
    TimelineRow,
    TimelineSection,
} from "../components/HistoryInspection/types.js";
import type { FileHistoryView, HistoryView } from "./ports.js";
import { bool, fields, list, num, str } from "./wireShape.js";

// The three-way transparency label; anything unrecognised degrades to "app"
// (a system moment) rather than silently reading as the author's.
const toActor = (raw: string | undefined): TimelineActor =>
    raw === "you" || raw === "assistant" ? raw : "app";

// A concise absolute time-of-day for a row ("10:01"). The section carries the
// date, so the row needs only the time. Parsed from the ISO string — no clock.
const formatWhen = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? iso
        : d.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
          });
};

// The date-section label ("18 August 2026"). Grouping is a render concern —
// buckets the flat chronological stream by calendar day.
const dateLabel = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? iso
        : d.toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
          });
};

// Map one wire row into its typed `TimelineRow` variant. Unknown / malformed
// rows return `undefined` and are dropped (graceful degradation).
const toRow = (raw: unknown): TimelineRow | undefined => {
    const f = fields(raw);
    if (f === undefined) return undefined;
    const kind = str(f.get("kind"));
    const id = str(f.get("eventId"));
    const at = str(f.get("at"));
    if (kind === undefined || id === undefined || at === undefined) {
        return undefined;
    }
    const subject = str(f.get("subject")) ?? "Untitled";
    const resourceKey = str(f.get("resourceKey"));
    const meta = {
        id,
        actor: toActor(str(f.get("actor"))),
        when: formatWhen(at),
        ...(resourceKey === undefined ? {} : { resourceKey }),
    };
    switch (kind) {
        case "ingested_source":
            return { type: "ingested_source", subject, ...meta };
        case "authored_material":
            return { type: "authored_material", subject, ...meta };
        case "edited_material":
            return {
                type: "edited_material",
                subject,
                edits: num(f.get("edits")) ?? 1,
                ...meta,
            };
        case "packaged_material":
            return { type: "packaged_material", subject, ...meta };
        case "deleted_resource":
            return { type: "deleted_resource", subject, ...meta };
        case "restored_resource":
            return { type: "restored_resource", subject, ...meta };
        case "version_restored":
            return {
                type: "version_restored",
                subject,
                fromVersion: `Version ${str(f.get("fromVersion")) ?? "?"}`,
                ...meta,
            };
        case "project_started":
            return { type: "project_started", subject, ...meta };
        case "session_started":
            return { type: "session_started", subject, ...meta };
        case "asserted_fact":
            return { type: "asserted_fact", subject, ...meta };
        case "retracted_fact":
            return { type: "retracted_fact", subject, ...meta };
        default:
            return undefined;
    }
};

// Group the flat, newest-first row stream into consecutive date sections.
const sectionsOf = (
    entries: readonly unknown[],
): readonly TimelineSection[] => {
    const sections: { label: string; rows: TimelineRow[] }[] = [];
    for (const raw of entries) {
        const at = str(fields(raw)?.get("at"));
        const row = toRow(raw);
        if (row === undefined || at === undefined) continue;
        const label = dateLabel(at);
        const last = sections.at(-1);
        if (last === undefined || last.label !== label) {
            sections.push({ label, rows: [row] });
        } else {
            last.rows.push(row);
        }
    }
    return sections;
};

const buildHistoryView = (value: unknown): HistoryView => {
    // Folded oldest→newest; the timeline reads newest-first.
    const entries = [...list(fields(value)?.get("entries"))].reverse();
    return {
        sections: sectionsOf(entries),
        // Inspection lenses served empty over HTTP (see the header shortcut).
        knowsYou: { milestones: [], frame: [] },
        triples: [],
        groundingElements: [],
        groundingCoverage: { grounded: 0, weak: 0, ungrounded: 0 },
    };
};

const buildFileHistoryView = (value: unknown): FileHistoryView => {
    const f = fields(value);
    const resourceTitle = str(f?.get("resourceTitle")) ?? "Untitled";
    // Server lists oldest→newest; the version panel reads newest-first (the
    // current version at the top).
    const versions = [...list(f?.get("versions"))]
        .flatMap((raw): readonly VersionEntry[] => {
            const vf = fields(raw);
            const id = str(vf?.get("id"));
            const versionLabel = str(vf?.get("versionLabel"));
            const when = str(vf?.get("when"));
            if (id === undefined || versionLabel === undefined) return [];
            return [
                {
                    id,
                    versionLabel,
                    when: when === undefined ? "" : formatWhen(when),
                    actor: toActor(str(vf?.get("actor"))),
                    summary: str(vf?.get("summary")) ?? "",
                    isCurrent: bool(vf?.get("isCurrent")),
                },
            ];
        })
        .reverse();
    // Diffs served empty over HTTP (the diff projection isn't wired yet); the
    // version list + restore work regardless.
    return { resourceTitle, versions, diffs: {} };
};
